import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { strategies, plans, type StrategyTemplate, type StrategyPass } from '../api/client'
import { Check, Zap, ShieldCheck, Gauge, Activity, SlidersHorizontal, Plus, Trash2 } from 'lucide-react'
import clsx from 'clsx'

interface StrategyPickerProps {
  projectId: string
  currentStrategyId: string | null
  /** Se readonly=true, não permite alterar (ex: plano já em execução) */
  readonly?: boolean
}

const STRATEGY_ICONS: Record<string, React.ReactNode> = {
  'Conservador': <ShieldCheck className="w-5 h-5" />,
  'Padrão': <Gauge className="w-5 h-5" />,
  'Agressivo': <Zap className="w-5 h-5" />,
  'Maratona': <Activity className="w-5 h-5" />,
}

const STRATEGY_COLORS: Record<string, string> = {
  'Conservador': 'border-blue-400 bg-blue-50',
  'Padrão': 'border-green-400 bg-green-50',
  'Agressivo': 'border-orange-400 bg-orange-50',
  'Maratona': 'border-purple-400 bg-purple-50',
}

/** Passada editável (sem `order`, que é derivado da posição). */
type DraftPass = Omit<StrategyPass, 'order'>

interface CustomDraft {
  passes: DraftPass[]
  maxSafetyAttempts: number
  safetyParallelism: number
}

/** Escolhe o template "mais agressivo" pra semear o custom: maior P na 1ª passada. */
function pickMostAggressive(templates: StrategyTemplate[]): StrategyTemplate | null {
  if (templates.length === 0) return null
  return [...templates].sort((a, b) => {
    const pa = a.passes[0]?.parallelism ?? 0
    const pb = b.passes[0]?.parallelism ?? 0
    return pb - pa
  })[0]
}

function draftFromTemplate(t: StrategyTemplate): CustomDraft {
  return {
    passes: t.passes
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((p) => ({
        parallelism: p.parallelism,
        delayBetweenSavesMs: p.delayBetweenSavesMs,
        maxRetriesPerMessage: p.maxRetriesPerMessage,
        initialBackoffSeconds: p.initialBackoffSeconds,
      })),
    maxSafetyAttempts: t.maxSafetyAttempts,
    safetyParallelism: t.safetyParallelism,
  }
}

const clampInt = (v: number, min: number, max: number) =>
  Number.isFinite(v) ? Math.max(min, Math.min(max, Math.round(v))) : min

export function StrategyPicker({ projectId, currentStrategyId, readonly = false }: StrategyPickerProps) {
  const queryClient = useQueryClient()
  const [customOpen, setCustomOpen] = useState(false)
  const [draft, setDraft] = useState<CustomDraft | null>(null)

  const { data: templates, isLoading } = useQuery({
    queryKey: ['strategies', 'templates'],
    queryFn: () => strategies.listTemplates(),
  })

  // Se a estratégia atual NÃO é um template, é uma custom já atribuída.
  const isCustomActive = !!currentStrategyId && !!templates && !templates.some((t) => t.id === currentStrategyId)

  // Detalhe da custom atual (pra semear o editor com os valores em uso).
  const { data: currentCustom } = useQuery({
    queryKey: ['strategy', currentStrategyId],
    queryFn: () => strategies.get(currentStrategyId!),
    enabled: isCustomActive,
  })

  const assignMutation = useMutation({
    mutationFn: (strategyId: string) => plans.assignStrategy(projectId, strategyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', projectId] })
    },
  })

  // Cria a custom (clonada do template mais agressivo) e atribui ao plano.
  const applyCustomMutation = useMutation({
    mutationFn: async (d: CustomDraft) => {
      const base = pickMostAggressive(templates ?? [])
      const stamp = new Date().toISOString().slice(0, 16).replace('T', ' ')
      const created = await strategies.createCustom({
        name: `Personalizado ${stamp}`,
        description: 'Configuração manual do operador',
        basedOnTemplateId: base?.id,
        maxSafetyAttempts: d.maxSafetyAttempts,
        safetyParallelism: d.safetyParallelism,
        passes: d.passes,
      })
      await plans.assignStrategy(projectId, created.id)
      return created.id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', projectId] })
      setCustomOpen(false)
    },
  })

  if (isLoading) return <div className="text-gray-500">Carregando estratégias...</div>
  if (!templates) return <div className="text-red-600">Erro ao carregar estratégias</div>

  function openCustomEditor() {
    if (readonly) return
    // Semeia do custom atual (se houver) ou do template mais agressivo.
    const seed = isCustomActive && currentCustom
      ? draftFromTemplate(currentCustom)
      : draftFromTemplate(pickMostAggressive(templates!) ?? templates![0])
    setDraft(seed)
    setCustomOpen(true)
  }

  function patchPass(idx: number, patch: Partial<DraftPass>) {
    setDraft((d) => {
      if (!d) return d
      const passes = d.passes.map((p, i) => (i === idx ? { ...p, ...patch } : p))
      return { ...d, passes }
    })
  }

  function addPass() {
    setDraft((d) => {
      if (!d) return d
      const last = d.passes[d.passes.length - 1]
      const next: DraftPass = last
        ? { ...last, parallelism: Math.max(1, Math.ceil(last.parallelism / 2)) }
        : { parallelism: 3, delayBetweenSavesMs: 80, maxRetriesPerMessage: 5, initialBackoffSeconds: 30 }
      return { ...d, passes: [...d.passes, next] }
    })
  }

  function removePass(idx: number) {
    setDraft((d) => {
      if (!d || d.passes.length <= 1) return d
      return { ...d, passes: d.passes.filter((_, i) => i !== idx) }
    })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="text-lg font-semibold">Estratégia de Migração</h3>
        {readonly && (
          <span className="text-xs text-gray-500 italic">
            Estratégia congelada (plano em execução)
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {templates.map((t) => {
          const selected = t.id === currentStrategyId
          const color = STRATEGY_COLORS[t.name] || 'border-gray-300 bg-white'
          return (
            <button
              key={t.id}
              type="button"
              disabled={readonly || assignMutation.isPending}
              onClick={() => assignMutation.mutate(t.id)}
              className={clsx(
                'relative text-left p-4 rounded-lg border-2 transition-all',
                selected ? `${color} ring-2 ring-offset-2 ring-blue-500` : 'border-gray-200 bg-white hover:border-gray-400',
                readonly && 'cursor-not-allowed opacity-70',
                !readonly && !assignMutation.isPending && 'cursor-pointer hover:shadow-md',
              )}
            >
              {selected && (
                <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
                  <Check className="w-3 h-3" />
                </div>
              )}

              <div className="flex items-center gap-2 mb-2">
                {STRATEGY_ICONS[t.name] || <Gauge className="w-5 h-5" />}
                <span className="font-semibold">{t.name}</span>
              </div>

              {t.description && (
                <p className="text-sm text-gray-600 mb-3">{t.description}</p>
              )}

              <div className="text-xs space-y-0.5 text-gray-700">
                <div>
                  <span className="font-medium">Passadas:</span>{' '}
                  {t.passes.map((p) => `P=${p.parallelism}`).join(' → ')}
                </div>
                <div>
                  <span className="font-medium">Safety:</span>{' '}
                  {t.maxSafetyAttempts}× P={t.safetyParallelism}
                </div>
                <div className="text-gray-500">
                  Total máximo: <strong>{t.totalMaxPasses}</strong> passadas
                </div>
              </div>
            </button>
          )
        })}

        {/* Card Personalizado — base no mais agressivo, campos editáveis pelo operador */}
        <button
          type="button"
          disabled={readonly}
          onClick={openCustomEditor}
          className={clsx(
            'relative text-left p-4 rounded-lg border-2 border-dashed transition-all',
            isCustomActive ? 'border-rose-400 bg-rose-50 ring-2 ring-offset-2 ring-blue-500' : 'border-gray-300 bg-white hover:border-gray-400',
            readonly ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:shadow-md',
          )}
        >
          {isCustomActive && (
            <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1">
              <Check className="w-3 h-3" />
            </div>
          )}
          <div className="flex items-center gap-2 mb-2">
            <SlidersHorizontal className="w-5 h-5" />
            <span className="font-semibold">Personalizado</span>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Parte do card mais agressivo e deixa você ajustar o paralelismo por mensagem, delay, retries e safety manualmente.
          </p>
          <div className="text-xs text-gray-700">
            {isCustomActive && currentCustom ? (
              <span>
                <span className="font-medium">Em uso:</span>{' '}
                {currentCustom.passes.map((p) => `P=${p.parallelism}`).join(' → ')}
              </span>
            ) : (
              <span className="text-gray-500">Clique para configurar</span>
            )}
          </div>
        </button>
      </div>

      {/* Editor da estratégia personalizada */}
      {customOpen && draft && (
        <div className="rounded-lg border-2 border-rose-300 bg-rose-50/40 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4" /> Configuração manual
            </h4>
            <span className="text-xs text-gray-500">Paralelismo por mensagem = mensagens simultâneas por conta</span>
          </div>

          {/* Passadas */}
          <div className="space-y-2">
            <div className="grid grid-cols-[2rem_1fr_1fr_1fr_1fr_2rem] gap-2 text-xs font-medium text-gray-600 px-1">
              <span>#</span>
              <span>Paralelismo por mensagem</span>
              <span>Delay (ms)</span>
              <span>Retries/msg</span>
              <span>Backoff (s)</span>
              <span></span>
            </div>
            {draft.passes.map((p, idx) => (
              <div key={idx} className="grid grid-cols-[2rem_1fr_1fr_1fr_1fr_2rem] gap-2 items-center">
                <span className="text-sm text-gray-500 text-center">{idx + 1}</span>
                <input
                  type="number" min={1} max={50} value={p.parallelism}
                  onChange={(e) => patchPass(idx, { parallelism: clampInt(+e.target.value, 1, 50) })}
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                />
                <input
                  type="number" min={0} max={60000} value={p.delayBetweenSavesMs}
                  onChange={(e) => patchPass(idx, { delayBetweenSavesMs: clampInt(+e.target.value, 0, 60000) })}
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                />
                <input
                  type="number" min={0} max={50} value={p.maxRetriesPerMessage}
                  onChange={(e) => patchPass(idx, { maxRetriesPerMessage: clampInt(+e.target.value, 0, 50) })}
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                />
                <input
                  type="number" min={0} max={3600} value={p.initialBackoffSeconds}
                  onChange={(e) => patchPass(idx, { initialBackoffSeconds: clampInt(+e.target.value, 0, 3600) })}
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                />
                <button
                  type="button"
                  onClick={() => removePass(idx)}
                  disabled={draft.passes.length <= 1}
                  title="Remover passada"
                  className="text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addPass}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
            >
              <Plus className="w-4 h-4" /> Adicionar passada
            </button>
          </div>

          {/* Safety */}
          <div className="grid grid-cols-2 gap-3 border-t border-rose-200 pt-3">
            <label className="text-sm">
              <span className="block font-medium text-gray-700 mb-1">Safety: tentativas</span>
              <input
                type="number" min={0} max={20} value={draft.maxSafetyAttempts}
                onChange={(e) => setDraft((d) => d && ({ ...d, maxSafetyAttempts: clampInt(+e.target.value, 0, 20) }))}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="block font-medium text-gray-700 mb-1">Safety: paralelismo</span>
              <input
                type="number" min={1} max={50} value={draft.safetyParallelism}
                onChange={(e) => setDraft((d) => d && ({ ...d, safetyParallelism: clampInt(+e.target.value, 1, 50) }))}
                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
              />
            </label>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={applyCustomMutation.isPending}
              onClick={() => draft && applyCustomMutation.mutate(draft)}
              className="px-4 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {applyCustomMutation.isPending ? 'Aplicando...' : 'Aplicar configuração'}
            </button>
            <button
              type="button"
              onClick={() => setCustomOpen(false)}
              className="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50"
            >
              Cancelar
            </button>
            {applyCustomMutation.isError && (
              <span className="text-sm text-red-600">Erro ao aplicar. Plano precisa estar em Draft/Approved.</span>
            )}
          </div>
        </div>
      )}

      {assignMutation.isError && (
        <div className="text-sm text-red-600">
          Erro ao atribuir estratégia. Verifique se o plano está em status Draft ou Approved.
        </div>
      )}
      {assignMutation.isSuccess && (
        <div className="text-sm text-green-600">✓ Estratégia atualizada</div>
      )}
      {applyCustomMutation.isSuccess && (
        <div className="text-sm text-green-600">✓ Configuração personalizada aplicada</div>
      )}
    </div>
  )
}
